import { useState, type ReactNode } from 'react';
import { ReplaceConfirmDialog } from './ReplaceConfirmDialog';
import { ImageNameDialog } from './ImageNameDialog';
import type { ProductImageSlot } from './productImageSlots';

/** 差替元が素材のとき、その素材 (SVG 原本を案件画像に引き継ぐために使う)。URL 入力や Amazon は undefined */
export type ReplaceSourceMaterial = { id: string; folderType: 'temporary' | 'cloud'; contentType: string | null };

type Options = {
  /** 差替確認ダイアログの見出し (例: Amazonの画像 1を差し替えますか？) */
  replaceTitle: (slot: ProductImageSlot) => string;
  /**
   * 差替の確定。戻り値の Promise が settle (resolve / reject どちらでも) したらダイアログを閉じる。
   * 送信を開始して即 resolve する関数を渡せば即閉じ、完了を await する関数を渡せば完了後に閉じる。
   * 通知 (トースト)・キャッシュ更新はこの関数の中で行う。
   */
  onConfirmReplace: (slot: ProductImageSlot, newUrl: string, source?: ReplaceSourceMaterial) => Promise<void>;
  /**
   * 名前の保存 (空文字 = クリア)。resolve したら閉じる。reject したら閉じず、入力を保持する。
   * 通知 (トースト)・キャッシュ更新はこの関数の中で行い、失敗は reject で伝える。
   */
  onSaveName: (slot: ProductImageSlot, name: string) => Promise<void>;
};

/**
 * 商品画像の差替確認・名前編集ダイアログの状態と描画 (Amazon / マニュアル共通)。
 * ページは「確定時に何を保存するか」だけを渡す。reject は捕捉して開閉だけを制御し、通知はページが行う。
 * 計画: docs/requirements/product-detail-page-consolidation-plan.md 4.4
 */
export function useProductImageDialogs({ replaceTitle, onConfirmReplace, onSaveName }: Options) {
  // 開いた時点の枠を控える (閉じるまで同じ内容を出す)
  const [replaceRequest, setReplaceRequest] = useState<{ slot: ProductImageSlot; newUrl: string; source?: ReplaceSourceMaterial } | null>(null);
  const [nameRequest, setNameRequest] = useState<{ slot: ProductImageSlot } | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);

  const openReplaceConfirm = (slot: ProductImageSlot, newUrl: string, source?: ReplaceSourceMaterial) =>
    setReplaceRequest({ slot, newUrl, source });
  const closeReplaceConfirm = () => setReplaceRequest(null);
  const openNameEdit = (slot: ProductImageSlot) => setNameRequest({ slot });
  const closeNameEdit = () => {
    if (isSavingName) return;
    setNameRequest(null);
  };

  const confirmReplace = async () => {
    const request = replaceRequest;
    if (!request) return;
    try {
      await onConfirmReplace(request.slot, request.newUrl, request.source);
    } catch {
      // 失敗の通知はページ側 (onConfirmReplace の中)。ここでは閉じるだけ
    } finally {
      setReplaceRequest(null);
    }
  };

  const saveName = async (name: string) => {
    const request = nameRequest;
    if (!request || isSavingName) return;
    setIsSavingName(true);
    try {
      await onSaveName(request.slot, name);
      setNameRequest(null);
    } catch {
      // 失敗時は閉じず入力を残す (通知はページ側)
    } finally {
      setIsSavingName(false);
    }
  };

  const dialogs: ReactNode = (
    <>
      {/* 画像差替確認モーダル */}
      {replaceRequest && (
        <ReplaceConfirmDialog
          title={replaceTitle(replaceRequest.slot)}
          originalUrl={replaceRequest.slot.url}
          newUrl={replaceRequest.newUrl}
          onConfirm={confirmReplace}
          onCancel={closeReplaceConfirm}
        />
      )}

      {/* 画像の名前編集モーダル */}
      {nameRequest && (
        <ImageNameDialog
          imageLabel={nameRequest.slot.label}
          initialName={nameRequest.slot.name || ''}
          isSaving={isSavingName}
          onSave={saveName}
          onCancel={closeNameEdit}
        />
      )}
    </>
  );

  return {
    openReplaceConfirm,
    closeReplaceConfirm,
    openNameEdit,
    closeNameEdit,
    isSavingName,
    /** 枠の見出しのインライン編集から直接保存する (モーダルを開かない)。失敗はそのまま投げる (入力を残す) */
    saveNameInline: onSaveName,
    /** 開いているダイアログ (ページの末尾に置く) */
    dialogs,
  };
}
